import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const SIZE_CLASSES = {
  xs: { root: "h-6 w-6", text: "text-[10px]" },
  sm: { root: "h-8 w-8", text: "text-xs" },
  md: { root: "h-10 w-10", text: "text-sm" },
  lg: { root: "h-12 w-12", text: "text-base" },
  xl: { root: "h-16 w-16", text: "text-lg" },
  "2xl": { root: "h-20 w-20 sm:h-24 sm:w-24", text: "text-xl" },
} as const;

export type UserAvatarSize = keyof typeof SIZE_CLASSES;

type UserAvatarProps = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  displayName?: string | null;
  imageSrc?: string | null;
  size?: UserAvatarSize;
  className?: string;
};

function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
  displayName?: string | null
): string {
  const first = firstName?.trim();
  const last = lastName?.trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();

  const name = displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  }

  const mail = email?.trim();
  if (mail) return mail.slice(0, 2).toUpperCase();
  return "?";
}

function hashString(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return hash >>> 0;
}

function buildGradient(seed: string): string {
  if (!seed) {
    return "linear-gradient(135deg, hsl(220, 10%, 60%), hsl(220, 10%, 45%))";
  }
  const hash = hashString(seed.toLowerCase());
  const hueStart = hash % 360;
  const hueEnd = (hueStart + 40 + ((hash >> 8) % 40)) % 360;
  return `linear-gradient(135deg, hsl(${hueStart}, 65%, 52%), hsl(${hueEnd}, 68%, 40%))`;
}

export function UserAvatar({
  firstName,
  lastName,
  email,
  displayName,
  imageSrc,
  size = "md",
  className,
}: UserAvatarProps): JSX.Element {
  const initials = getInitials(firstName, lastName, email, displayName);
  const seed =
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    displayName?.trim() ||
    email?.trim() ||
    "";
  const gradient = buildGradient(seed);
  const alt = displayName?.trim() || email?.trim() || "User";
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <Avatar className={cn(sizeClasses.root, className)}>
      {imageSrc ? <AvatarImage src={imageSrc} alt={alt} /> : null}
      <AvatarFallback
        className={cn("font-semibold text-white", sizeClasses.text)}
        style={{ backgroundImage: gradient }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
